"use client";

import React, { useState, useEffect } from "react";

import CloseIcon from "@/public/admin-sidebar/close.svg";
import FigmaIcon from "@/public/admin-sidebar/figma.svg";
import VercelIcon from "@/public/admin-sidebar/vercel.svg";
import TinaIcon from "@/public/admin-sidebar/tina.svg";
import GoogleDriveIcon from "@/public/admin-sidebar/google-drive.svg";
import GoogleAnalyticsIcon from "@/public/admin-sidebar/google-analytics.svg";
import GoogleTagManagerIcon from "@/public/admin-sidebar/google-tag-manager.svg";
import HubspotIcon from "@/public/admin-sidebar/hubspot.svg";
import GoogleSearchConsoleIcon from "@/public/admin-sidebar/google-search-console.svg";
import GithubIcon from "@/public/admin-sidebar/github.svg";
import SecureFrameIcon from "@/public/admin-sidebar/secureframe.svg";

import { cn } from "@/utils";
import { BookmarkIcon } from "lucide-react";

const AdminSidebar = () => {
  return (
    // <EditProvider>
    <AdminSidebarInner />
    // </EditProvider>
  );
};

export const useIsEditing = () => {
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isInIframe = window.self !== window.top;
      setIsEditing(isInIframe);
    }
  }, []);

  return isEditing;
};

const AdminSidebarInner = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isEditing = useIsEditing();
  const showAdminSidebar = isEditing;
  if (!showAdminSidebar) return null;

  return (
    <>
      <div
        className={cn(
          "admin-dashboard fixed top-6 right-0 bg-secondary-600 hover:bg-secondary-600/90 z-[60] rounded-l-[4px] cursor-pointer h-10 w-[48px] flex justify-center items-center"
        )}
        onClick={() => setDrawerOpen(!drawerOpen)}
      >
        {drawerOpen ? (
          <CloseIcon className="w-6 h-6 text-white ml-1" />
        ) : (
          <BookmarkIcon className="w-6 h-6 stroke-1 text-white ml-1" />
        )}
      </div>
      <div
        className={cn(
          "fixed top-0 -right-1 h-screen z-base bg-white z-50 shadow-sm border-l border-gray-100 "
        )}
      >
        <div
          className={cn(
            "relative h-screen transform flex transition-all duration-300 ease-out overflow-hidden",
            drawerOpen ? "max-w-[450px] z-50" : "max-w-[1px]"
          )}
        >
          <div className="p-6 min-w-[450px] overflow-hidden flex flex-col gap-6 divide-y divide-black/10">
            <div className="flex-[2] space-y-4 overflow-auto">
              <div className="font-semibold text-xl select-none text-black">
                Bookmarks
              </div>
              <div className="space-y-3">
                <ResourceLink
                  icon={GoogleDriveIcon}
                  iconClassName="w-9 h-9"
                  title="Google Drive"
                  subtitle="Useful guides and resources"
                  href="https://drive.google.com/drive/folders/10tGSJ1AddkkIhkUcIxo-ve29CD3r9Abv?usp=drive_link"
                />
                <ResourceLink
                  icon={FigmaIcon}
                  iconClassName="w-9 h-9"
                  title="Designs"
                  subtitle="View the website mockups in Figma"
                  href="https://www.figma.com/design/xbGDxeXYOpFDyNQkY0tdn1/-EXT--Binarly?node-id=1276-2376&t=A1tuXlqOczrF4UPF-0"
                />
                <ResourceLink
                  icon={VercelIcon}
                  iconClassName="w-8 h-8"
                  title="Hosting & Analytics"
                  subtitle="Hosting & Analytics for the website"
                  href="https://vercel.com/binarly-inc/binarly-website/analytics?environment=all"
                />
                <ResourceLink
                  icon={GoogleAnalyticsIcon}
                  iconClassName="w-8 h-8"
                  title="Google Analytics"
                  subtitle="Analytics for the website"
                  href="hhttps://analytics.google.com/analytics/web/#/a227119159p537490072/reports/intelligenthome"
                />
                <ResourceLink
                  icon={GoogleTagManagerIcon}
                  iconClassName="w-8 h-8"
                  title="Google Tag Manager"
                  subtitle="Manage and deploy tags to your website"
                  href="https://tagmanager.google.com/#/container/accounts/6354041181/containers/251713685/workspaces/10"
                />
                <ResourceLink
                  icon={GoogleSearchConsoleIcon}
                  iconClassName="w-9 h-9"
                  title="Google Search Console"
                  subtitle="Monitor your site's visibility on Google"
                  href="https://search.google.com/search-console?resource_id=sc-domain%3Abinarly.io"
                />
                <ResourceLink
                  icon={GithubIcon}
                  iconClassName="w-9 h-9"
                  title="Github"
                  subtitle="Website Repo"
                  href="https://github.com/comediadesign/binarly-website"
                />
                <ResourceLink
                  icon={GithubIcon}
                  iconClassName="w-9 h-9"
                  title="Github Actions"
                  subtitle="Automated workflows for Advisory Sync"
                  href="https://github.com/comediadesign/binarly-website/actions"
                />
                <ResourceLink
                  icon={TinaIcon}
                  iconClassName="w-9 h-9"
                  title="CMS"
                  subtitle="Tina Content Management System"
                  href="https://app.tina.io"
                />
                <ResourceLink
                  icon={HubspotIcon}
                  iconClassName="w-9 h-9"
                  title="Hubspot"
                  subtitle="Book Demo form & newsletter subscriptions"
                  href="https://www.hubspot.com/"
                />
                {/* <ResourceLink
                  icon={BeehiivIcon}
                  iconClassName="w-8 h-8"
                  title="Beehiiv"
                  subtitle="Email marketing for Newsletters"
                  href="https://www.beehiv.com/"
                /> */}
                <ResourceLink
                  icon={SecureFrameIcon}
                  iconClassName="w-8 h-8"
                  title="Secure Frame"
                  subtitle="Compliance and security tools (Trust center)"
                  href="https://secureframe.com/"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;

const ResourceLink = ({
  title,
  subtitle,
  icon: Icon,
  iconClassName = "w-7 h-7",
  href = "",
  disabled = false,
}: {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  href?: string;
  disabled?: boolean;
}) => {
  return (
    <a
      className={cn(
        "border border-gray-100 flex items-center gap-3 px-3 py-3 rounded",
        disabled
          ? "opacity-60 cursor-not-allowed"
          : "cursor-pointer hover:bg-gray-100/40"
      )}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="w-12 h-12 flex items-center justify-center">
        <Icon className={iconClassName} />
      </div>
      <div className="space-y-0.5">
        <div className="font-title text-black font-semibold">{title}</div>
        <div className="text-gray-600 text-sm">{subtitle}</div>
      </div>
    </a>
  );
};
